import { AlertCircle, X } from 'lucide-react';
import { FORM_RADIUS } from './tokens';

interface FormErrorAlertProps {
  readonly error: string | null;
  /** Si se pasa, se muestra la X para descartar el aviso. */
  readonly onClear?: () => void;
  /** Acción de recuperación junto al mensaje, ej. reintentar la carga que falló. */
  readonly action?: {
    readonly label: string;
    readonly onClick: () => void;
  };
  /**
   * Solo separación externa (`mb-4` y similares), para los contenedores que no aportan
   * `space-y-*`. No usarlo para colores, bordes ni padding: eso es lo que se unificó acá.
   * El margen vive dentro del componente a propósito — un div envolvente dejaría el hueco
   * abierto también cuando no hay error.
   */
  readonly spacingClassName?: string;
}

/**
 * Aviso de error dentro de un formulario. Único lugar donde vive este recuadro: antes
 * estaba escrito a mano en ~30 modales con 23 combinaciones distintas de padding, radio
 * e icono, así que cambiar cómo se ve un error de formulario obligaba a editarlos todos.
 *
 * Solo para errores que el usuario puede corregir (validación). Los fallos de sistema van
 * por `reportarErrorInesperado`, que los captura y los muestra en un toast reportable.
 */
export function FormErrorAlert({ error, onClear, action, spacingClassName = '' }: Readonly<FormErrorAlertProps>) {
  if (!error) return null;

  return (
    <div
      role="alert"
      className={`flex items-start justify-between gap-3 p-3 bg-red-50 border border-red-200 ${FORM_RADIUS} ${spacingClassName}`}
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="text-sm font-semibold text-red-800 underline hover:text-red-900"
          >
            {action.label}
          </button>
        )}
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-red-500 hover:text-red-700"
            aria-label="Descartar el aviso de error"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
